<script setup lang="ts">
import {inject, ref} from "vue";
import {Avatar, Button, Dialog, Menu} from "@/Components/Library";
import dayjs from "dayjs";
import FormattedResponseData from "./FormattedResponseData.vue";
import {Question} from "@/Types/question";
import User from "@/Models/User.ts";

const $user = new User();
const $date = inject("date") as typeof dayjs;
const emit = defineEmits<{
    responseClick: any;
    edit: any;
    delete: any;
}>();

const props = defineProps<{
    question: Question;
    hasFilters?: boolean;
}>();
const showMore = ref(false);

const showResponseMenu = ref(false);
const selectedResponse = ref(null);
const responseMenuItems = ref([
    {
        label: "Edit",
        icon: "prime:pencil",
        command: () => {
            emit("edit", selectedResponse.value);
            showResponseMenu.value = false;
        },
    },
    {
        label: "Delete",
        icon: "prime:trash",
        class: "text-red-600 hover:bg-red-100",
        command: () => {
            emit("delete", selectedResponse.value);
            showResponseMenu.value = false;
        },
    },
]);

const openResponseMenu = (response) => {
    selectedResponse.value = response;
    showResponseMenu.value = true;
};

const questionIsPublished =
    $date(props.question.question.publish_from).isBefore(new Date()) &&
    (props.question.question.publish_to === null || $date(props.question.question.publish_to).isAfter(new Date()));
</script>
<template>
    <div>
        <div v-if="!question.responses.length" class="w-full p-8">
            <p class="text-center text-stone-500">
                {{ hasFilters ? "No responses found for these filters." : "No responses yet." }}
            </p>
        </div>
        <div v-else class="mb-2 flex flex-col gap-3" v-auto-animate>
            <template v-for="(response, i) in question.responses" :key="response.id">
                <div
                    v-if="i < 10 || showMore"
                    class="flex flex-col gap-1 rounded-lg bg-stone-100 p-3 dark:bg-stone-900"
                    :class="{
                        'lg:items--center':
                            question.type.slug !== 'text' &&
                            question.type.slug !== 'weighting' &&
                            !Array.isArray(response.data),
                    }"
                    @click="emit('responseClick', response.id)"
                >
                    <div class="mb-3 flex items-center gap-3" v-if="response.user">
                        <div class="flex flex-none items-center gap-2 lg:mb-0">
                            <Avatar
                                :user="response.user"
                                class="h-8 w-8 flex-none rounded-full p-1 text-xs"
                                shape="circle"
                            />
                            <h6 class="font-bold dark:text-stone-400">{{ response.user.name }}</h6>
                        </div>
                        <span class="ml-auto flex-none text-xs">
                            {{ dayjs(response.created_at).format("DD/MM/YYYY HH:mm") }}
                        </span>
                        <Button
                            v-if="question.group?.can_admin || response.user_id === $user.id"
                            type="button"
                            :icon="{
                                icon: 'pepicons-pencil:dots-y',
                                width: '24',
                            }"
                            class="h-8 w-8 flex-none !ring-0"
                            @click.stop="openResponseMenu(response)"
                        />
                    </div>
                    <div class="flex items-center">
                        <FormattedResponseData :question="question" :response="response" />
                    </div>
                </div>
            </template>
            <Button
                v-if="question.responses.length > 10"
                @click="showMore = !showMore"
                class="w-full !py-0 !ring-0"
                :icon="{icon: showMore ? 'mdi:chevron-up' : 'mdi:chevron-down', width: 32}"
            />
        </div>
        <Dialog
            v-model:visible="showResponseMenu"
            modal
            dismissable-mask
            class="max-w-xs overflow-hidden"
            :closable="false"
            :show-header="false"
        >
            <template #container>
                <div class="bg-white">
                    <Menu ref="menu" :model="responseMenuItems" />
                </div>
            </template>
        </Dialog>
    </div>
</template>
<style scoped></style>
